import { Box, Paper, Typography, Button, CircularProgress, IconButton } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { GoogleMap, useJsApiLoader, Polygon, Marker, Circle, OverlayView } from '@react-google-maps/api'
import HudBanner from './ui/HudBanner'
import countyCoordinates from './countyCoordinates'
import countyPolygons from './countyPolygons'
import apiService from '../services/api'
import { APPLICATION_TYPES, getApplicationStyle, getPrimaryApplicationType, sortCategoryNamesForDisplay } from './applicationStyles'
import { getTimelineEventList } from '../utils/timelineEvents'
import { lastActivitySortKey } from '../utils/lastActivitySortKey'
import { parseLocalDateString } from '../utils/localDate'
import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { createLogger } from '../utils/logger'
import { addMapBreadcrumb } from '../utils/sentryTracking'
import { posthog, isUserIdentified } from '../services/posthog'
import {
  CHARLOTTE_METRO_DEFAULT,
  CHARLOTTE_METRO_COUNTY_SLUGS,
  centerFromRegionBoundaries,
  readStoredMapViewport,
  writeStoredMapViewport,
} from './mapDefaults'
import { PANEL_CHROME_HEADER_H } from '../layout/appChrome'

const logger = createLogger('MapView')

const GOOGLE_MAP_LIBRARIES = ['geometry']
const ZOOM_STYLE_THRESHOLD = 12

function attachSentryMapListeners(map, setZoomStyleTier, fallbackZoom, { onIdle } = {}) {
  let idleTimer;
  const scheduleIdle = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      try {
        const center = map.getCenter?.();
        addMapBreadcrumb(map.getZoom?.(), center ? { lat: center.lat(), lng: center.lng() } : null);
        onIdle?.(map);
      } catch {}
    }, 300);
  };
  try {
    const initialZoom = map.getZoom ? map.getZoom() : fallbackZoom;
    setZoomStyleTier(initialZoom >= ZOOM_STYLE_THRESHOLD ? 'near' : 'far');
    map.addListener('zoom_changed', () => {
      try {
        const z = map.getZoom();
        const tier = z >= ZOOM_STYLE_THRESHOLD ? 'near' : 'far';
        setZoomStyleTier((prev) => (prev === tier ? prev : tier));
      } catch {}
    });
    map.addListener('dragend', scheduleIdle);
    map.addListener('idle', scheduleIdle);
  } catch {}
}


const containerStyle = {
  width: '100%',
  height: '230px',
  borderRadius: '8px',
}

const center = {
  lat: CHARLOTTE_METRO_DEFAULT.lat,
  lng: CHARLOTTE_METRO_DEFAULT.lng,
}

const CIRCLE_RADIUS = 20000; // meters, adjust as needed for visual effect

const PURPLE = '#7454A5'
const MUNI_COLOR = '#26A69A' // teal for municipality boundaries

// moved to applicationStyles.js

// Dark minimal style.
// IMPORTANT: Google Maps silently drops styles if the JSON gets too long.
// Keep this short to ensure it actually applies.
const darkMinimalRoadmapStyle = [
  // Overall palette (slightly lighter than pure-black to avoid "void" feeling)
  { elementType: 'geometry', stylers: [{ color: '#1f1f1f' }] },
  // Kill *all* labels (including highway shields)
  { elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },

  // Remove the square "grid" layers (parcels / local boundaries)
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },

  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  // Kill the “grid” look: hide most roads; keep highways only.
  { featureType: 'road', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', stylers: [{ visibility: 'on' }] },
  // Highways: lighter lines, but still no labels/shields
  { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4a4a4a' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a1a' }] },

  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0d0d0d' }] },
];

// Zoomed-in dark style: show streets (still no labels)
const darkZoomedInRoadmapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1f1f1f' }] },
  { elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
  // Roads (no labels): subtle hierarchy
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2f2f2f' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#151515' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#565656' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a1a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0d0d0d' }] },
];

// Minimal styles to reduce visual noise.
// NOTE: Google enforces a max character limit on style arrays; if exceeded, NO styles apply.
// Keep these arrays short and rely on overlays (parcels/boundaries) for emphasis.
const minimalistRoadmapStyle = [
  // Kill all labels (names) and label icons (business/places markers)
  { elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  // Extra suppression for POIs/transit layers
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const minimalistSatelliteStyle = [
  { elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

// Google Maps API Key from environment variable
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY

function BoundaryLayerPill({ label, selected, onClick, accentColor }) {
  return (
    <Button
      onClick={onClick}
      aria-pressed={selected}
      size="small"
      sx={{
        textTransform: 'none',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.4,
        px: 1.75,
        py: 0.5,
        minHeight: 28,
        borderRadius: '999px',
        color: selected ? '#fff' : '#444',
        bgcolor: selected ? accentColor : '#fff',
        border: '1px solid',
        borderColor: selected ? accentColor : '#ddd',
        boxShadow: selected ? `0 0 0 1px ${accentColor}` : '0 1px 2px rgba(0,0,0,0.06)',
        '&:hover': {
          bgcolor: selected ? accentColor : '#f5f5f5',
          borderColor: selected ? accentColor : '#ccc',
        },
      }}
    >
      {label}
    </Button>
  );
}

function MapTopRightControls({
  mapType,
  onMapTypeChange,
  showCountyBoundaries,
  onCountyChange,
  showMuniBoundaries,
  onMuniChange,
  compact = false,
  sx,
}) {
  return (
    <Box
      component="nav"
      aria-label="Map display options"
      sx={{
        position: 'absolute',
        top: compact ? 8 : 10,
        right: compact ? 'auto' : 10,
        left: compact ? 8 : 'auto',
        zIndex: 2000,
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: compact ? 'stretch' : 'center',
        gap: compact ? 0.75 : 1,
        flexWrap: 'wrap',
        flexDirection: compact ? 'column' : 'row',
        justifyContent: compact ? 'flex-start' : 'flex-end',
        maxWidth: compact ? 'min(220px, calc(100% - 16px))' : 'calc(100% - 20px)',
        ...sx,
      }}
    >
      <BoundaryLayerPill
        label={compact ? 'Counties' : 'County boundaries'}
        selected={showCountyBoundaries}
        onClick={() => onCountyChange(!showCountyBoundaries)}
        accentColor={PURPLE}
      />
      <BoundaryLayerPill
        label={compact ? 'Municipalities' : 'Municipality boundaries'}
        selected={showMuniBoundaries}
        onClick={() => onMuniChange(!showMuniBoundaries)}
        accentColor={MUNI_COLOR}
      />
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          borderRadius: '2px',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          alignSelf: compact ? 'flex-start' : undefined,
        }}
      >
        {[
          { type: 'roadmap', label: 'Map' },
          { type: 'satellite', label: compact ? 'Sat' : 'Satellite' },
        ].map(({ type, label }, index) => {
          const selected = mapType === type;
          return (
            <Button
              key={type}
              onClick={() => onMapTypeChange(type)}
              aria-pressed={selected}
              sx={{
                minWidth: compact ? 52 : 72,
                py: 0.75,
                px: compact ? 1 : 1.5,
                fontSize: compact ? 12 : 13,
                fontWeight: selected ? 600 : 400,
                textTransform: 'none',
                borderRadius: 0,
                bgcolor: selected ? '#fff' : '#f1f1f1',
                color: '#333',
                borderRight: index === 0 ? '1px solid #ddd' : 'none',
                '&:hover': { bgcolor: selected ? '#fff' : '#e4e4e4' },
              }}
            >
              {label}
            </Button>
          );
        })}
      </Paper>
    </Box>
  );
}

// Example polygon for Plymouth County MA (approximate, demo only)
const plymouthCountyPolygon = [
  { lat: 42.1160, lng: -70.8100 }, // NE
  { lat: 42.1160, lng: -70.6000 }, // E
  { lat: 41.9000, lng: -70.6000 }, // SE
  { lat: 41.9000, lng: -70.8100 }, // SW
  { lat: 42.1160, lng: -70.8100 }, // back to NE
]

// This is now populated from our generated file
// const countyPolygons = {
//   'Plymouth County MA': plymouthCountyPolygon,
//   // Add more counties here as needed
// }

function pointInRing(lat, lng, ring) {
  if (!ring || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].lat;
    const xi = ring[i].lng;
    const yj = ring[j].lat;
    const xj = ring[j].lng;
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

function computePathsListBBox(pathsList) {
  let minLat = 90;
  let maxLat = -90;
  let minLng = 180;
  let maxLng = -180;
  let any = false;
  for (const entry of pathsList || []) {
    for (const p of entry.paths || []) {
      if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue;
      any = true;
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }
  }
  return any ? { minLat, maxLat, minLng, maxLng } : null;
}

function compareApplicationsNewestFirst(a, b) {
  const diff = lastActivitySortKey(b) - lastActivitySortKey(a)
  if (diff !== 0) return diff
  return String(a.case_number || a.id || '').localeCompare(String(b.case_number || b.id || ''))
}

/** Oldest / undated render first (bottom); newest on top. Selected case always on top. */
function compareParcelLayersForRender(a, b, selectedAppId) {
  const aSel = selectedAppId != null && a.appId === selectedAppId
  const bSel = selectedAppId != null && b.appId === selectedAppId
  if (aSel && !bSel) return 1
  if (!aSel && bSel) return -1
  const diff = lastActivitySortKey(a.application) - lastActivitySortKey(b.application)
  if (diff !== 0) return diff
  return String(a.appId).localeCompare(String(b.appId))
}

function parcelContainsPoint(latLng, parcel) {
  const g = window.google;
  if (!g?.maps) return false;

  const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
  const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;

  const bbox = parcel.bbox;
  if (bbox && (lat < bbox.minLat || lat > bbox.maxLat || lng < bbox.minLng || lng > bbox.maxLng)) {
    return false;
  }

  for (const entry of parcel.pathsList || []) {
    const ring = entry.paths;
    if (g.maps.geometry?.poly?.containsLocation) {
      if (g.maps.geometry.poly.containsLocation(latLng, new g.maps.Polygon({ paths: ring }))) {
        return true;
      }
    } else if (pointInRing(lat, lng, ring)) {
      return true;
    }
  }
  return false;
}

function findApplicationsAtParcelPoint(latLng, parcels) {
  const seen = new Set();
  const hits = [];
  for (const parcel of parcels) {
    if (seen.has(parcel.appId)) continue;
    if (parcelContainsPoint(latLng, parcel)) {
      seen.add(parcel.appId);
      hits.push(parcel.application);
    }
  }
  hits.sort(compareApplicationsNewestFirst);
  return hits;
}

function getCaseTypeLabels(app) {
  if (Array.isArray(app?.category_names) && app.category_names.length > 0) {
    return sortCategoryNamesForDisplay(app.category_names);
  }
  if (app?.application_type) return [app.application_type];
  return [];
}

function CaseHoverTooltipContent({ applications, frozen, onCaseClick, selectedApplicationId }) {
  if (!applications?.length) return null;
  const multi = applications.length > 1;

  return (
    <div
      onContextMenu={(e) => { if (frozen) e.preventDefault(); }}
      style={{
        background: 'rgba(15, 15, 15, 0.94)',
        border: `1.5px solid ${frozen ? '#a78bfa' : '#7454A5'}`,
        borderRadius: 8,
        padding: '8px 10px',
        maxWidth: 300,
        boxShadow: frozen ? '0 4px 16px rgba(0,0,0,0.65)' : '0 2px 10px rgba(0,0,0,0.55)',
      }}
    >
      {applications.map((app, idx) => {
        const types = getCaseTypeLabels(app);
        const caseLabel = app.case_number
          ? `Case ${app.case_number}`
          : (app.application_name || `Report ${app.id}`);
        const isSelected = selectedApplicationId != null && app.id === selectedApplicationId;
        const rowStyle = {
          marginTop: idx > 0 ? 8 : 0,
          paddingTop: idx > 0 ? 8 : 0,
          borderTop: idx > 0 ? '1px solid rgba(255,255,255,0.12)' : 'none',
          display: 'block',
          width: '100%',
          textAlign: 'left',
          background: isSelected ? 'rgba(116,84,165,0.28)' : 'transparent',
          border: 'none',
          borderRadius: 4,
          padding: frozen ? '4px 6px' : 0,
          marginLeft: frozen ? -6 : 0,
          marginRight: frozen ? -6 : 0,
          cursor: frozen ? 'pointer' : 'default',
          transition: 'background 0.12s ease',
        };

        const content = (
          <>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 700, marginBottom: types.length ? 4 : 0, lineHeight: 1.3 }}>
              {caseLabel}
            </div>
            {types.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {types.map((type) => {
                  const c = APPLICATION_TYPES[type]?.labelColor || APPLICATION_TYPES[type]?.color || '#7454A5';
                  return (
                    <span
                      key={type}
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: c,
                        background: `${c}22`,
                        padding: '2px 6px',
                        borderRadius: 3,
                        lineHeight: '14px',
                      }}
                    >
                      {type.toUpperCase()}
                    </span>
                  );
                })}
              </div>
            ) : null}
          </>
        );

        if (frozen && onCaseClick) {
          return (
            <button
              key={app.id || idx}
              type="button"
              style={rowStyle}
              onClick={() => onCaseClick(app)}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isSelected ? 'rgba(116,84,165,0.28)' : 'transparent';
              }}
            >
              {content}
            </button>
          );
        }

        return (
          <div key={app.id || idx} style={rowStyle}>
            {content}
          </div>
        );
      })}
      {!frozen && multi ? (
        <div style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          color: 'rgba(255,255,255,0.55)',
          fontSize: 10,
          lineHeight: 1.4,
        }}>
          Right-click to select a case
        </div>
      ) : null}
      {frozen ? (
        <div style={{
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          color: 'rgba(255,255,255,0.55)',
          fontSize: 10,
          lineHeight: 1.4,
        }}>
          Click a case · Esc or click map to close
        </div>
      ) : null}
    </div>
  );
}

function MapCaseHoverOverlay({ applications, pixel, frozen, onCaseClick, selectedApplicationId }) {
  if (!applications?.length || !pixel) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        left: pixel.x + 14,
        top: pixel.y - 8,
        zIndex: frozen ? 10001 : 10000,
        pointerEvents: frozen ? 'auto' : 'none',
        transform: 'translateY(-50%)',
      }}
    >
      <CaseHoverTooltipContent
        applications={applications}
        frozen={frozen}
        onCaseClick={onCaseClick}
        selectedApplicationId={selectedApplicationId}
      />
    </Box>
  );
}

function ParcelPolygonLayers({ parcel, isSelected, zIndex: zIndexProp, onClick, onMouseOver, onMouseMove, onMouseOut, onRightClick, interactive = true }) {
  const appStyle = getApplicationStyle(getPrimaryApplicationType(parcel.application));
  const strokeWeight = isSelected ? 4 : 3;
  const fillOpacity = isSelected ? 0.55 : 0.32;
  const zIndex = zIndexProp ?? (isSelected ? 2000 : 400);
  const keyPrefix = interactive ? 'parcel' : 'parcel-small';

  return (parcel.pathsList || []).map((entry) => (
    <Polygon
      key={`${keyPrefix}-${entry.key}`}
      paths={entry.paths}
      options={{
        fillColor: appStyle.color,
        fillOpacity,
        strokeColor: appStyle.color,
        strokeOpacity: 0.9,
        strokeWeight,
        clickable: interactive,
        zIndex,
      }}
      onClick={interactive ? onClick : undefined}
      onMouseOver={interactive ? onMouseOver : undefined}
      onMouseMove={interactive ? onMouseMove : undefined}
      onMouseOut={interactive ? onMouseOut : undefined}
      onRightClick={interactive ? onRightClick : undefined}
    />
  ));
}

function MapView({
  county,
  dateRange,
  dataTypes,
  backgroundMode,
  selectedProperty,
  applications = [],
  playbackDate = null,
  includeAllDates = true,
  embedded = false,
  mapHeight,
  hiddenCount = 0,
  onPinClick,
  selectedApplication,
  regions = [],
  selectedRegionSlug = null,
  viewportCountySlugs = [],
  onRegionSelect,
  accessibleRegionSlugs = [],
  dataReady = true,
  isVisible = true,
  onReady,
  showExpandFilters = false,
  onExpandFilters,
  compactChrome = false,
  /** Increment to re-run pan/zoom to the selected case (same as first select). */
  mapFocusNonce = 0,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: GOOGLE_MAP_LIBRARIES,
  })
  const mapRef = useRef(null)
  const storedViewportRef = useRef(readStoredMapViewport())
  const [mapCenter, setMapCenter] = useState(() =>
    storedViewportRef.current
      ? { lat: storedViewportRef.current.lat, lng: storedViewportRef.current.lng }
      : center
  )
  const [mapZoom, setMapZoom] = useState(() => storedViewportRef.current?.zoom ?? CHARLOTTE_METRO_DEFAULT.zoom)
  const [showCountyBoundaries, setShowCountyBoundaries] = useState(true)
  const [showMuniBoundaries, setShowMuniBoundaries] = useState(false)
  const [mapType, setMapType] = useState('roadmap') // 'roadmap' | 'satellite'
  const [zoomStyleTier, setZoomStyleTier] = useState(
    () => ((storedViewportRef.current?.zoom ?? CHARLOTTE_METRO_DEFAULT.zoom) >= ZOOM_STYLE_THRESHOLD ? 'near' : 'far')
  )
  const prevCountyRef = useRef(county)
  const prevRegionSlugRef = useRef(selectedRegionSlug)
  const prevPropertyRef = useRef(selectedProperty)
  const defaultViewportAppliedRef = useRef(!!storedViewportRef.current)
  const viewportPersistEnabledRef = useRef(false)
  const initialReadyRef = useRef(false)
  const mapIdleListenerRef = useRef(null)
  const zoomIntervalRef = useRef(null)
  const [mapInstanceReady, setMapInstanceReady] = useState(false)
  const [boundariesFetchComplete, setBoundariesFetchComplete] = useState(false)
  const [pinsProcessed, setPinsProcessed] = useState(false)
  const [mapTilesReady, setMapTilesReady] = useState(false)
  const [isInitiallyReady, setIsInitiallyReady] = useState(false)
  const [viewportRevision, setViewportRevision] = useState(0)
  const [geocodedPins, setGeocodedPins] = useState([])
  const [parcelPolygons, setParcelPolygons] = useState([]) // [{ key, appId, application, paths }]
  const markerRefs = useRef({})
  const [hoveredPin, setHoveredPin] = useState(null)
  const [flashPinId, setFlashPinId] = useState(null)
  const [flashOn, setFlashOn] = useState(false)
  const flashTimersRef = useRef([])
  const [hoveredParcelHit, setHoveredParcelHit] = useState(null);
  const [pinnedParcelPicker, setPinnedParcelPicker] = useState(null);
  const pinnedParcelPickerRef = useRef(null);
  const [hoveredRegionSlug, setHoveredRegionSlug] = useState(null);
  const [regionBoundaries, setRegionBoundaries] = useState({}); // { [slug]: paths[][] }
  const [muniBoundaryPaths, setMuniBoundaryPaths] = useState({}); // { [muniId]: paths[][] }
  const [muniBoundaryNames, setMuniBoundaryNames] = useState({}); // { [muniId]: name }
  const [muniBoundaryPublished, setMuniBoundaryPublished] = useState({}); // { [muniId]: bool }
  const [muniBoundariesFetched, setMuniBoundariesFetched] = useState(false);
  const [hoveredMuniId, setHoveredMuniId] = useState(null);
  const accessibleSlugSet = useMemo(
    () => new Set(accessibleRegionSlugs || []),
    [accessibleRegionSlugs]
  );

  const persistViewportFromMap = useCallback((map) => {
    if (!viewportPersistEnabledRef.current || !map) return;
    try {
      const c = map.getCenter?.();
      const z = map.getZoom?.();
      if (!c || z == null) return;
      const lat = c.lat();
      const lng = c.lng();
      writeStoredMapViewport({ lat, lng, zoom: z });
      setMapCenter({ lat, lng });
      setMapZoom(z);
      setZoomStyleTier(z >= ZOOM_STYLE_THRESHOLD ? 'near' : 'far');
    } catch {}
  }, []);

  useEffect(() => {
    if (mapInstanceReady) viewportPersistEnabledRef.current = true;
  }, [mapInstanceReady]);

  const polygonOptions = useMemo(() => ({
    fillColor: PURPLE,
    fillOpacity: 0.25,
    strokeColor: PURPLE,
    strokeOpacity: 0.8,
    strokeWeight: 2,
    clickable: true,
    zIndex: 0,
  }), []);

  const hoveredPolygonOptions = useMemo(() => ({
    fillColor: PURPLE,
    fillOpacity: 0.5,
    strokeColor: PURPLE,
    strokeOpacity: 0.9,
    strokeWeight: 3,
    clickable: true,
    zIndex: 1,
  }), []);

  // Compute top-center of a polygon's bounding box (good anchor for a floating label)
  const getPathsBBoxTopCenter = (paths) => {
    if (!paths || paths.length === 0) return null;
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    paths.forEach(ring => ring.forEach(p => {
      if (p.lat < minLat) minLat = p.lat;
      if (p.lat > maxLat) maxLat = p.lat;
      if (p.lng < minLng) minLng = p.lng;
      if (p.lng > maxLng) maxLng = p.lng;
    }));
    return { lat: maxLat, lng: (minLng + maxLng) / 2 };
  };

  // Reusable polygon hover tooltip
  const PolygonTooltip = ({ position, label, accentColor }) => (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(w) => ({ x: -Math.round(w / 2), y: 6 })}
    >
      <div style={{ pointerEvents: 'none', display: 'flex', justifyContent: 'center' }}>
        <div style={{
          background: 'rgba(15, 15, 15, 0.94)',
          border: `1.5px solid ${accentColor}`,
          borderRadius: 6,
          padding: '4px 10px',
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
        }}>
          <span style={{ color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: '16px' }}>{label}</span>
        </div>
      </div>
    </OverlayView>
  );

  // Helper: convert GeoJSON (Polygon/MultiPolygon/Feature/FeatureCollection) into Google Maps paths
  const geoJsonToPaths = (geojson) => {
    if (!geojson || typeof geojson !== 'object') return [];

    const geometries = [];
    const pushGeom = (g) => {
      if (g && typeof g === 'object') geometries.push(g);
    };

    if (geojson.type === 'FeatureCollection' && Array.isArray(geojson.features)) {
      geojson.features.forEach((f) => f && f.geometry && pushGeom(f.geometry));
    } else if (geojson.type === 'Feature' && geojson.geometry) {
      pushGeom(geojson.geometry);
    } else if (geojson.type === 'Polygon' || geojson.type === 'MultiPolygon') {
      pushGeom(geojson);
    }

    const paths = [];

    geometries.forEach((geom) => {
      if (!geom || !Array.isArray(geom.coordinates)) return;
      if (geom.type === 'Polygon') {
        geom.coordinates.forEach((ring) => {
          if (Array.isArray(ring)) {
            paths.push(
              ring.map(([lng, lat]) => ({ lat, lng }))
            );
          }
        });
      } else if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((polygon) => {
          polygon.forEach((ring) => {
            if (Array.isArray(ring)) {
              paths.push(
                ring.map(([lng, lat]) => ({ lat, lng }))
              );
            }
          });
        });
      }
    });

    return paths;
  };

  // Helper: extract parcel polygons for a report entry.
  // Expected app.parcel_geometries: [{ parcel_id, status, geometry, centroid_lat, centroid_lng }, ...]
  const getParcelPolygonPaths = (app) => {
    const items = app?.parcel_geometries;
    if (!Array.isArray(items) || items.length === 0) return [];
    const out = [];
    items.forEach((it, idx) => {
      const geom = it?.geometry;
      const paths = geoJsonToPaths(geom);
      paths.forEach((ring, ringIdx) => {
        if (Array.isArray(ring) && ring.length >= 3) {
          out.push({ key: `${app.id || app.case_number || 'app'}-${idx}-${ringIdx}`, paths: ring });
        }
      });
    });
    return out;
  };

  const fitMapToPaths = (pathsList) => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    let any = false;
    pathsList.forEach((ring) => {
      ring.forEach((p) => {
        if (p && typeof p.lat === 'number' && typeof p.lng === 'number') {
          bounds.extend(p);
          any = true;
        }
      });
    });
    if (any) {
      try {
        map.fitBounds(bounds, { top: 64, right: 64, bottom: 64, left: 64 });
      } catch {
        // fallback if padding signature differs
        map.fitBounds(bounds);
      }
    }
  };

  // Helper: compute the approximate center of a region's bounds
  const getRegionCenter = (slug) => {
    const paths = regionBoundaries[slug];
    if (!paths || paths.length === 0) return null;
    let minLat = 90;
    let maxLat = -90;
    let minLng = 180;
    let maxLng = -180;
    paths.forEach((ring) => {
      ring.forEach((p) => {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
      });
    });
    if (maxLat <= minLat || maxLng <= minLng) return null;
    const latCenter = (maxLat + minLat) / 2;
    const lngCenter = (minLng + maxLng) / 2;
    return { lat: latCenter, lng: lngCenter };
  };

  // Fetch county boundaries for all known regions (user-accessible) and cache in state
  useEffect(() => {
    if (!regions || regions.length === 0) {
      setBoundariesFetchComplete(false);
      return;
    }

    let cancelled = false;

    const slugsToFetch = regions
      .map((r) => r.slug)
      .filter(
        (slug) => slug && !(slug in regionBoundaries)
      );

    if (slugsToFetch.length === 0) {
      setBoundariesFetchComplete(true);
      return;
    }

    setBoundariesFetchComplete(false);

    const fetchBoundaries = async () => {
      const t0 = performance.now();
      try {
        const results = await Promise.all(
          slugsToFetch.map(async (slug) => {
            try {
              const geojson = await apiService.getCountyBoundary(slug);
              const paths = geoJsonToPaths(geojson);
              return { slug, paths };
            } catch (err) {
              logger.apiFailure('Failed to fetch boundary for region', {
                view: 'map',
                action: 'load_county_boundary',
                county_slug: slug,
              }, err);
              return { slug, paths: [] };
            }
          })
        );

        if (cancelled) return;

        const updates = {};
        results.forEach((entry) => {
          if (entry?.slug) {
            updates[entry.slug] = entry.paths || [];
          }
        });

        if (Object.keys(updates).length > 0) {
          setRegionBoundaries((prev) => ({ ...prev, ...updates }));
          logger.info('Region boundaries loaded', {
            region_count: Object.keys(updates).length,
            slugs: Object.keys(updates).join(','),
            load_time_ms: Math.round(performance.now() - t0),
          });
        }
      } catch (err) {
        logger.apiFailure('Failed to fetch region boundaries', {
          view: 'map',
          action: 'load_county_boundaries',
          region_count: slugsToFetch.length,
        }, err);
      } finally {
        if (!cancelled) {
          setBoundariesFetchComplete(true);
        }
      }
    };

    fetchBoundaries();

    return () => {
      cancelled = true;
    };
  }, [regions, regionBoundaries]);

  // Fetch municipality boundaries once when the toggle is first enabled
  useEffect(() => {
    if (!showMuniBoundaries || muniBoundariesFetched) return;
    let cancelled = false;
    const fetchMunis = async () => {
      try {
        const data = await apiService.getMunicipalityBoundaries();
        if (cancelled) return;
        const parsed = {};
        const names = {};
        const pubMap = data.published || {};
        for (const [muniId, geojson] of Object.entries(data.boundaries || {})) {
          const paths = geoJsonToPaths(geojson);
          if (paths.length > 0) {
            parsed[muniId] = paths;
            const feat = geojson?.features?.[0];
            if (feat?.properties?.name) names[muniId] = feat.properties.name;
          }
        }
        setMuniBoundaryPaths(parsed);
        setMuniBoundaryNames(names);
        setMuniBoundaryPublished(pubMap);
        setMuniBoundariesFetched(true);
      } catch (err) {
        logger.apiFailure('Failed to fetch municipality boundaries', {
          view: 'map',
          action: 'load_municipality_boundaries',
        }, err);
      }
    };
    fetchMunis();
    return () => { cancelled = true; };
  }, [showMuniBoundaries, muniBoundariesFetched]);

  // Parse a label like "Cobb County GA" to { countyName: "Cobb County", stateAbbr: "GA" }
  const parseCountyState = (label) => {
    if (!label || typeof label !== 'string') return { countyName: '', stateAbbr: 'GA' }
    const trimmed = label.replace(/\s*,\s*/g, ' ').trim()
    // Try patterns: "Cobb County GA" or "Cobb County, GA"
    const m = trimmed.match(/^(.*?)(?:\s+County)?\s+(AL|GA|NC|SC|TN|FL)\b/i)
    if (m) {
      const name = (m[1] || '').trim()
      const abbr = (m[2] || 'GA').toUpperCase()
      const countyName = name.endsWith('County') ? name : (name ? `${name} County` : '')
      return { countyName, stateAbbr: abbr }
    }
    // Fallback: if already like "Cobb County", assume GA
    if (/county/i.test(trimmed)) return { countyName: trimmed, stateAbbr: 'GA' }
    return { countyName: '', stateAbbr: 'GA' }
  }

  // Compute bounding box for current county polygon
  const getCountyBounds = (label) => {
    const poly = countyPolygons[label]
    if (!poly || poly.length === 0) return null
    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180
    for (const p of poly) {
      if (p.lat < minLat) minLat = p.lat
      if (p.lat > maxLat) maxLat = p.lat
      if (p.lng < minLng) minLng = p.lng
      if (p.lng > maxLng) maxLng = p.lng
    }
    return { sw: { lat: minLat, lng: minLng }, ne: { lat: maxLat, lng: maxLng } }
  }

  // Compute polygon centroid using the shoelace formula (area-weighted)
  const getCountyCentroid = (label) => {
    const poly = countyPolygons[label]
    if (!poly || poly.length < 3) return null
    let twiceArea = 0
    let cx = 0
    let cy = 0
    for (let i = 0, n = poly.length; i < n; i++) {
      const p1 = poly[i]
      const p2 = poly[(i + 1) % n]
      const xi = p1.lng, yi = p1.lat
      const xj = p2.lng, yj = p2.lat
      const a = xi * yj - xj * yi
      twiceArea += a
      cx += (xi + xj) * a
      cy += (yi + yj) * a
    }
    if (Math.abs(twiceArea) < 1e-12) {
      // fallback avg
      let sumLat = 0, sumLng = 0
      for (const p of poly) { sumLat += p.lat; sumLng += p.lng }
      return { lat: sumLat / poly.length, lng: sumLng / poly.length }
    }
    const area = twiceArea / 2
    cx = cx / (6 * area)
    cy = cy / (6 * area)
    return { lat: cy, lng: cx }
  }

  // Compute anchor slightly above county top, horizontally at the configured county center
  const getCountyLabelAnchor = (label) => {
    const bounds = getCountyBounds(label)
    if (!bounds) return null
    const latSpan = bounds.ne.lat - bounds.sw.lat
    // Use the same lng the map pans to when selecting this county (from countyCoordinates)
    const lngCenter = (countyCoordinates[label] && typeof countyCoordinates[label].lng === 'number')
      ? countyCoordinates[label].lng
      : (bounds.ne.lng + bounds.sw.lng) / 2
    const latAbove = bounds.ne.lat + latSpan * 0.06
    return { anchor: { lat: latAbove, lng: lngCenter }, latSpan }
  }

  // Point in polygon (ray casting) to verify result lies within selected county
  const isPointInCounty = (label, point) => {
    const poly = countyPolygons[label]
    if (!poly || !point) return true // if unknown, don't block
    let inside = false
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].lng, yi = poly[i].lat
      const xj = poly[j].lng, yj = poly[j].lat
      const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
        (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi + 1e-12) + xi)
      if (intersect) inside = !inside
    }
    return inside
  }

  // Compute a simple confidence score from a geocoding result
  const computeGeocodeConfidence = (result) => {
    if (!result) return 0;
    const lt = result.geometry?.location_type || 'APPROXIMATE';
    const types = Array.isArray(result.types) ? result.types : [];
    const partial = !!result.partial_match;
    // base by location_type
    let score = 0.7;
    if (lt === 'ROOFTOP') score = 0.99;
    else if (lt === 'RANGE_INTERPOLATED') score = 0.95;
    else if (lt === 'GEOMETRIC_CENTER') score = 0.85;
    else score = 0.7; // APPROXIMATE or unknown
    // type bonuses
    if (types.some(t => ['street_address','premise','subpremise'].includes(t))) score += 0.03;
    if (types.some(t => ['intersection','route'].includes(t))) score += 0.02;
    if (partial) score -= 0.10;
    // clamp
    if (score < 0) score = 0; if (score > 1) score = 1;
    return score;
  };

  // Extract simplified admin path for validation/logging
  const extractAdminPath = (components) => {
    const get = (type) => {
      const c = (components || []).find(ac => ac.types?.includes(type));
      return c?.long_name || null;
    };
    return {
      country: get('country'),
      state: get('administrative_area_level_1'),
      county: get('administrative_area_level_2'),
      locality: get('locality') || get('postal_town') || null,
    };
  };

  const ENABLE_GEOCODE_LOGGING = true;

  // Frontend geocoding disabled: pins use backend-provided lat/lng only

  // Helper to derive the first date when an application becomes visible on the map
  const getFirstVisibleDate = (app) => {
    let firstDateStr = null;
    const evs = getTimelineEventList(app.timeline);
    if (evs.length > 0) {
      const sorted = [...evs].sort((a, b) => {
        const da = parseLocalDateString(a.date)?.getTime() ?? 0;
        const db = parseLocalDateString(b.date)?.getTime() ?? 0;
        return da - db;
      });
      firstDateStr = sorted[0]?.date;
    }
    if (!firstDateStr) {
      firstDateStr = app.first_activity_date || app.initial_filing_date || app.created_at;
    }
    const d = firstDateStr ? parseLocalDateString(firstDateStr) : null;
    return d && !isNaN(d.getTime()) ? d : null;
  };

  // Process applications to create geocoded pins
  useEffect(() => {
    if (!dataReady) {
      setPinsProcessed(false);
      return;
    }

    if (!applications || applications.length === 0) {
      setGeocodedPins([]);
      setParcelPolygons([]);
      setPinsProcessed(true);
      return;
    }

    const processApplications = async () => {
      const t0 = performance.now();
      const pins = [];
      const polygons = [];
      
      for (const app of applications) {
        // Prefer parcel polygons over pins when available.
        const parcelPaths = getParcelPolygonPaths(app);
        if (parcelPaths.length > 0) {
          polygons.push({
            appId: app.id,
            application: app,
            pathsList: parcelPaths, // [{key, paths}]
            bbox: computePathsListBBox(parcelPaths),
            firstDate: getFirstVisibleDate(app),
          });
          continue;
        }

        const address = app.address;
        // Prefer precomputed backend coordinates if available
        const preLat = app.geocode_lat;
        const preLng = app.geocode_lng;
        if (typeof preLat === 'number' && typeof preLng === 'number') {
          pins.push({
            id: app.id,
            lat: preLat,
            lng: preLng,
            address: address,
            title: `${app.application_type || 'Report'} ${app.case_number || app.id}`,
            application: app,
            firstDate: getFirstVisibleDate(app),
            // Store app reference for click handling
            clickHandler: () => {
              logger.debug('Pin click handler called', {
                view: 'map',
                application_id: String(app.id),
                application_type: app.application_type,
              });
              if (isUserIdentified()) {
                posthog.capture('map_pin_clicked', {
                  report_id: app.id,
                  county: app.county_slug,
                  report_type: app.application_type,
                });
              }
              if (onPinClick) {
                onPinClick(app);
              }
            }
          });
          continue;
        }

        // If no backend coordinates, skip rendering this pin
      }
      
      setGeocodedPins(pins);
      setParcelPolygons(polygons);
      setPinsProcessed(true);

      const renderMs = Math.round(performance.now() - t0);
      logger.info('Map rendered', {
        pin_count: pins.length,
        polygon_count: polygons.length,
        application_count: applications.length,
        render_time_ms: renderMs,
      });
      if (renderMs > 1000) {
        logger.warn('Map render slow', {
          pin_count: pins.length,
          polygon_count: polygons.length,
          render_time_ms: renderMs,
        });
      }
    };

    processApplications();
  }, [applications, onPinClick, dataReady]);

  useEffect(() => {
    // Helper to clear any running intervals
    const clearZoomInterval = () => {
      if (zoomIntervalRef.current) {
        clearInterval(zoomIntervalRef.current)
        zoomIntervalRef.current = null
      }
    }

    // If a property is selected, animate to it
    if (selectedProperty && selectedProperty.lat && selectedProperty.lng) {
      clearZoomInterval()
      let currentZoom = mapZoom
      zoomIntervalRef.current = setInterval(() => {
        if (currentZoom > 4) {
          currentZoom -= 1
          setMapZoom(currentZoom)
        } else {
          clearZoomInterval()
          setMapCenter({ lat: selectedProperty.lat, lng: selectedProperty.lng })
          let targetZoom = selectedProperty.zoom || 17
          let zoomIn = 4
          zoomIntervalRef.current = setInterval(() => {
            if (zoomIn < targetZoom) {
              zoomIn += 1
              setMapZoom(zoomIn)
            } else {
              setMapZoom(targetZoom)
              clearZoomInterval()
            }
          }, 120)
          prevPropertyRef.current = selectedProperty
        }
      }, 100)
      return clearZoomInterval
    }

    // Pan to a single county when the user changes focused region — not on initial load.
    if (!selectedProperty && selectedRegionSlug && regionBoundaries[selectedRegionSlug]) {
      if (prevRegionSlugRef.current !== null && selectedRegionSlug !== prevRegionSlugRef.current) {
        clearZoomInterval();
        const centerPt = getRegionCenter(selectedRegionSlug);
        if (centerPt) {
          setMapCenter(centerPt);
          setMapZoom(10);
        }
      }
      prevRegionSlugRef.current = selectedRegionSlug;
      return clearZoomInterval;
    }

    // If county changed (legacy logic fallback)
    const mapHasBeenInitialized = prevCountyRef.current !== null && prevCountyRef.current !== undefined;
    if (!selectedProperty && county && (!mapHasBeenInitialized || county !== prevCountyRef.current)) {
      clearZoomInterval()
      let currentZoom = mapZoom
      zoomIntervalRef.current = setInterval(() => {
        if (currentZoom > 4) {
          currentZoom -= 1
          setMapZoom(currentZoom)
        } else {
          clearZoomInterval()
          if (county && countyCoordinates[county]) {
            setMapCenter({ lat: countyCoordinates[county].lat, lng: countyCoordinates[county].lng })
            let targetZoom = countyCoordinates[county].zoom
            let zoomIn = 4
            zoomIntervalRef.current = setInterval(() => {
              if (zoomIn < targetZoom) {
                zoomIn += 1
                setMapZoom(zoomIn)
              } else {
                setMapZoom(targetZoom)
                clearZoomInterval()
              }
            }, 120)
          } else {
            setMapCenter(center)
            setMapZoom(4)
          }
          prevCountyRef.current = county
        }
      }, 100)
      return clearZoomInterval
    }

    return clearZoomInterval
  }, [county, selectedProperty, selectedRegionSlug, regionBoundaries])

  // Fit map to all selected counties once boundaries (and map) are ready.
  useEffect(() => {
    if (defaultViewportAppliedRef.current || selectedProperty) return;
    if (!boundariesFetchComplete || !mapInstanceReady || !mapRef.current) return;

    const slugs = viewportCountySlugs.length > 0
      ? viewportCountySlugs
      : CHARLOTTE_METRO_COUNTY_SLUGS;
    const loadedSlugs = slugs.filter((s) => regionBoundaries[s]?.length);
    if (loadedSlugs.length === 0) return;

    const allPaths = loadedSlugs.flatMap((s) => regionBoundaries[s] || []);
    if (allPaths.length) {
      fitMapToPaths(allPaths);
      try {
        const c = mapRef.current.getCenter?.();
        const z = mapRef.current.getZoom?.();
        if (c && z != null) {
          setMapCenter({ lat: c.lat(), lng: c.lng() });
          setMapZoom(z);
        }
      } catch {
        const fallbackCenter = centerFromRegionBoundaries(regionBoundaries, loadedSlugs);
        if (fallbackCenter) {
          setMapCenter(fallbackCenter);
          setMapZoom(CHARLOTTE_METRO_DEFAULT.zoom);
        }
      }
    }

    defaultViewportAppliedRef.current = true;
    setViewportRevision((v) => v + 1);
  }, [boundariesFetchComplete, regionBoundaries, selectedProperty, viewportCountySlugs, mapInstanceReady]);

  useEffect(() => {
    if (initialReadyRef.current) return;
    setMapTilesReady(false);
    if (mapIdleListenerRef.current && typeof window !== 'undefined' && window.google?.maps?.event) {
      window.google.maps.event.removeListener(mapIdleListenerRef.current);
      mapIdleListenerRef.current = null;
    }
  }, [viewportRevision]);

  const waitForMapIdle = useCallback(() => {
    const map = mapRef.current;
    if (!map || typeof window === 'undefined' || !window.google?.maps?.event) {
      setMapTilesReady(true);
      return;
    }
    if (mapIdleListenerRef.current) return;

    const markTilesReady = () => {
      if (mapIdleListenerRef.current) {
        window.google.maps.event.removeListener(mapIdleListenerRef.current);
        mapIdleListenerRef.current = null;
      }
      setMapTilesReady(true);
    };

    mapIdleListenerRef.current = map.addListener('idle', markTilesReady);
    window.setTimeout(() => {
      if (!initialReadyRef.current) markTilesReady();
    }, 800);
  }, []);

  useEffect(() => {
    if (initialReadyRef.current) return;
    if (loadError) {
      initialReadyRef.current = true;
      setIsInitiallyReady(true);
      onReady?.();
      return;
    }
    if (!dataReady || !isLoaded || !boundariesFetchComplete || !pinsProcessed) return;
    if (!defaultViewportAppliedRef.current) return;

    if (!mapTilesReady) {
      waitForMapIdle();
      return;
    }

    initialReadyRef.current = true;
    setIsInitiallyReady(true);
    onReady?.();
  }, [
    dataReady,
    isLoaded,
    loadError,
    boundariesFetchComplete,
    pinsProcessed,
    mapTilesReady,
    viewportRevision,
    onReady,
    waitForMapIdle,
  ]);

  useEffect(() => () => {
    if (mapIdleListenerRef.current && typeof window !== 'undefined' && window.google?.maps?.event) {
      window.google.maps.event.removeListener(mapIdleListenerRef.current);
      mapIdleListenerRef.current = null;
    }
  }, []);

  const mapOptions = useMemo(() => ({
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    // Dark + minimal roadmap; satellite just suppresses labels/icons.
    styles: mapType === 'roadmap'
      ? (zoomStyleTier === 'near' ? darkZoomedInRoadmapStyle : darkMinimalRoadmapStyle)
      : minimalistSatelliteStyle,
    // Prevent default Google "place" icons from being clickable/interactive.
    clickableIcons: false,
    disableDoubleClickZoom: false,
    gestureHandling: 'greedy',
    mapTypeId: mapType,
  }), [mapType, zoomStyleTier])

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapInstanceReady) return;
    const c = map.getCenter?.();
    const z = map.getZoom?.();
    if (
      c
      && Math.abs(c.lat() - mapCenter.lat) < 1e-7
      && Math.abs(c.lng() - mapCenter.lng) < 1e-7
      && z != null
      && Math.abs(z - mapZoom) < 0.01
    ) {
      return;
    }
    map.setCenter(mapCenter);
  }, [mapCenter, mapInstanceReady, mapZoom]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapInstanceReady) return;
    const z = map.getZoom?.();
    if (z != null && Math.abs(z - mapZoom) < 0.01) return;
    map.setZoom(mapZoom);
    setZoomStyleTier(mapZoom >= ZOOM_STYLE_THRESHOLD ? 'near' : 'far');
  }, [mapZoom, mapInstanceReady]);

  // Dynamic click threshold in degrees based on zoom (rough heuristic)
  const getClickThresholdDeg = () => {
    const z = (mapRef.current && typeof mapRef.current.getZoom === 'function') ? mapRef.current.getZoom() : mapZoom;
    // Base ~0.0005 deg at zoom 16 (~50m), scale up as we zoom out
    const base = 0.0005;
    const factor = Math.pow(2, Math.max(0, 14 - (z || 14)));
    return base * factor;
  };

  // Convert a LatLng to pixel coordinates for the current zoom level
  const latLngToPixel = (map, latLng) => {
    try {
      const projection = map.getProjection();
      if (!projection || !window.google) return null;
      const worldPoint = projection.fromLatLngToPoint(latLng);
      const scale = Math.pow(2, map.getZoom());
      return { x: worldPoint.x * scale, y: worldPoint.y * scale };
    } catch {
      return null;
    }
  };

  // Find nearest pin to a given mouse/click position using pixel distance
  const findNearestPinByPixels = (map, latLng) => {
    const clickPx = latLngToPixel(map, latLng);
    if (!clickPx) return null;
    let nearest = null;
    let minDist = Number.POSITIVE_INFINITY;
    for (const pin of geocodedPins) {
      const pinLatLng = new window.google.maps.LatLng(pin.lat, pin.lng);
      const pinPx = latLngToPixel(map, pinLatLng);
      if (!pinPx) continue;
      const dx = pinPx.x - clickPx.x;
      const dy = pinPx.y - clickPx.y;
      const d = Math.hypot(dx, dy);
      if (d < minDist) {
        minDist = d;
        nearest = pin;
      }
    }
    return nearest ? { pin: nearest, distPx: minDist } : null;
  };

  // Shift a LatLng by a pixel delta at current zoom
  const shiftLatLngByPixels = (map, latLng, dxPx, dyPx) => {
    try {
      const proj = map.getProjection();
      if (!proj) return latLng;
      const pt = proj.fromLatLngToPoint(latLng);
      const scale = Math.pow(2, map.getZoom());
      const shifted = new window.google.maps.Point(
        pt.x + dxPx / scale,
        pt.y + dyPx / scale
      );
      return proj.fromPointToLatLng(shifted);
    } catch {
      return latLng;
    }
  };

  const mouseMoveListenerRef = useRef(null);
  const mapMouseLeaveHandlerRef = useRef(null);
  const visibleParcelPolygonsRef = useRef([]);

  const visibleParcelPolygons = useMemo(
    () => parcelPolygons.filter(
      (p) => includeAllDates || !playbackDate || (p.firstDate && p.firstDate <= playbackDate)
    ),
    [parcelPolygons, includeAllDates, playbackDate]
  );

  useEffect(() => {
    visibleParcelPolygonsRef.current = visibleParcelPolygons;
  }, [visibleParcelPolygons]);

  useEffect(() => {
    pinnedParcelPickerRef.current = pinnedParcelPicker;
  }, [pinnedParcelPicker]);

  useEffect(() => {
    if (!pinnedParcelPicker) return undefined;
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') setPinnedParcelPicker(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pinnedParcelPicker]);

  const dismissPinnedPicker = useCallback(() => {
    setPinnedParcelPicker(null);
  }, []);

  const handlePinnedCaseClick = useCallback((app) => {
    if (onPinClick) onPinClick(app);
    setPinnedParcelPicker(null);
  }, [onPinClick]);

  const handleParcelPolygonClick = useCallback((e) => {
    if (!onPinClick || !e?.latLng) return;
    const hits = findApplicationsAtParcelPoint(e.latLng, visibleParcelPolygonsRef.current);
    if (hits.length) onPinClick(hits[0]);
  }, [onPinClick]);

  const getParcelLayerZIndex = useCallback((parcel, renderIndex, isSelected) => {
    if (isSelected) return 2000;
    return 400 + renderIndex;
  }, []);

  const getHoverPixelFromEvent = useCallback((domEvent, latLng) => {
    const map = mapRef.current;
    if (!map?.getDiv) return null;
    try {
      if (domEvent) {
        const rect = map.getDiv().getBoundingClientRect();
        return { x: domEvent.clientX - rect.left, y: domEvent.clientY - rect.top };
      }
      if (latLng) {
        const bounds = map.getBounds();
        const mapDiv = map.getDiv();
        if (!bounds || !mapDiv) return null;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
        const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;
        const lngSpan = ne.lng() - sw.lng();
        const latSpan = ne.lat() - sw.lat();
        if (!lngSpan || !latSpan) return null;
        return {
          x: ((lng - sw.lng()) / lngSpan) * mapDiv.offsetWidth,
          y: ((ne.lat() - lat) / latSpan) * mapDiv.offsetHeight,
        };
      }
    } catch {}
    return null;
  }, []);

  const handleParcelPolygonContextMenu = useCallback((e) => {
    if (e?.domEvent) {
      e.domEvent.preventDefault();
      e.domEvent.stopPropagation?.();
    }
    if (!e?.latLng) return;
    const hits = findApplicationsAtParcelPoint(e.latLng, visibleParcelPolygonsRef.current);
    if (!hits.length) return;
    if (hits.length === 1) {
      if (onPinClick) onPinClick(hits[0]);
      setPinnedParcelPicker(null);
      return;
    }
    const pixel = getHoverPixelFromEvent(e.domEvent, e.latLng);
    setHoveredParcelHit(null);
    setPinnedParcelPicker({ applications: hits, pixel });
  }, [getHoverPixelFromEvent, onPinClick]);

  const renderCaseHoverOverlays = () => (
    <>
      {pinnedParcelPicker && (
        <MapCaseHoverOverlay
          applications={pinnedParcelPicker.applications}
          pixel={pinnedParcelPicker.pixel}
          frozen
          onCaseClick={handlePinnedCaseClick}
          selectedApplicationId={selectedApplication?.id}
        />
      )}
      {!compactChrome && !pinnedParcelPicker && (hoveredPin || hoveredParcelHit) && (
        <MapCaseHoverOverlay
          applications={hoveredPin ? [hoveredPin.application] : hoveredParcelHit.applications}
          pixel={hoveredPin?.pixel || hoveredParcelHit?.pixel}
          selectedApplicationId={selectedApplication?.id}
        />
      )}
    </>
  );

  const handleParcelPolygonHover = useCallback((e) => {
    if (pinnedParcelPickerRef.current) return;
    if (!e?.latLng) return;
    const hits = findApplicationsAtParcelPoint(e.latLng, visibleParcelPolygonsRef.current);
    if (!hits.length) return;
    setHoveredPin(null);
    const pixel = getHoverPixelFromEvent(e.domEvent, e.latLng);
    setHoveredParcelHit({ applications: hits, pixel });
    if (mapRef.current) mapRef.current.setOptions({ draggableCursor: 'pointer' });
  }, [getHoverPixelFromEvent]);

  const handleParcelPolygonHoverEnd = useCallback(() => {
    if (pinnedParcelPickerRef.current) return;
    setHoveredParcelHit(null);
    if (mapRef.current) mapRef.current.setOptions({ draggableCursor: '' });
  }, []);

  const attachMouseMoveListener = useCallback((map) => {
    if (!map || !window.google) return;
    if (mouseMoveListenerRef.current) {
      mouseMoveListenerRef.current.remove();
      mouseMoveListenerRef.current = null;
    }
    if (mapMouseLeaveHandlerRef.current) {
      try { map.getDiv()?.removeEventListener('mouseleave', mapMouseLeaveHandlerRef.current); } catch {}
      mapMouseLeaveHandlerRef.current = null;
    }

    const handleMouseLeave = () => {
      setHoveredParcelHit(null);
      setHoveredPin(null);
    };
    mapMouseLeaveHandlerRef.current = handleMouseLeave;
    try { map.getDiv()?.addEventListener('mouseleave', handleMouseLeave); } catch {}

    mouseMoveListenerRef.current = map.addListener('mousemove', (e) => {
      if (!e.latLng) return;
      const nearest = findNearestPinByPixels(map, e.latLng);
      const pinHit = nearest && nearest.distPx < 22;
      map.setOptions({ draggableCursor: pinHit ? 'pointer' : '' });
    });
  }, [geocodedPins]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    attachMouseMoveListener(map);
    return () => {
      if (mouseMoveListenerRef.current) {
        try { mouseMoveListenerRef.current.remove(); } catch {}
        mouseMoveListenerRef.current = null;
      }
      if (mapMouseLeaveHandlerRef.current) {
        try { map.getDiv()?.removeEventListener('mouseleave', mapMouseLeaveHandlerRef.current); } catch {}
        mapMouseLeaveHandlerRef.current = null;
      }
    };
  }, [attachMouseMoveListener, mapZoom]);

  // Trigger a brief white flash on the selected application pin
  useEffect(() => {
    // clear previous timers
    flashTimersRef.current.forEach(id => clearTimeout(id));
    flashTimersRef.current = [];
    setFlashOn(false);

    if (selectedApplication && selectedApplication.id) {
      setFlashPinId(selectedApplication.id);
      // slower, longer flashing: ~6 pulses over ~7.2s
      const period = 1200; // ms
      const onFraction = 0.45; // time ON within each period
      const pulses = 6;
      for (let i = 0; i < pulses; i++) {
        const tOn = setTimeout(() => setFlashOn(true), Math.floor(i * period));
        const tOff = setTimeout(() => setFlashOn(false), Math.floor(i * period + period * onFraction));
        flashTimersRef.current.push(tOn, tOff);
      }
      const tEnd = setTimeout(() => setFlashPinId(null), pulses * period + 100);
      flashTimersRef.current.push(tEnd);
    } else {
      setFlashPinId(null);
    }
    return () => {
      flashTimersRef.current.forEach(id => clearTimeout(id));
      flashTimersRef.current = [];
    };
  }, [selectedApplication]);

  // Pan/zoom to selected application's parcel polygon or geocode point.
  const pendingPanRef = useRef(null);
  const lastPannedIdRef = useRef(null);
  const lastPolyFitIdRef = useRef(null);
  const skipRestoredSelectionPanRef = useRef(!!storedViewportRef.current);
  const prevSelectedApplicationIdRef = useRef(null);

  const isRestoredSelectionWithCachedViewport = useCallback((applicationId) => {
    if (!skipRestoredSelectionPanRef.current) return false;
    if (prevSelectedApplicationIdRef.current != null) return false;
    try {
      const savedId = localStorage.getItem('ls_selectedApplicationId');
      if (!savedId || String(applicationId) !== savedId) return false;
      skipRestoredSelectionPanRef.current = false;
      return true;
    } catch {
      return false;
    }
  }, []);

  const markSelectionPanHandled = useCallback((applicationId) => {
    lastPolyFitIdRef.current = applicationId;
    lastPannedIdRef.current = applicationId;
    pendingPanRef.current = null;
    prevSelectedApplicationIdRef.current = applicationId;
  }, []);

  // Track selection changes — reset polygon fit tracking
  useEffect(() => {
    if (!selectedApplication?.id) {
      lastPolyFitIdRef.current = null;
      lastPannedIdRef.current = null;
      pendingPanRef.current = null;
      prevSelectedApplicationIdRef.current = null;
    } else if (lastPannedIdRef.current !== selectedApplication.id) {
      lastPolyFitIdRef.current = null;
    }
  }, [selectedApplication?.id]);

  // Fit to parcel polygon when available (fires once per selection)
  useEffect(() => {
    if (!selectedApplication?.id) return;
    if (lastPolyFitIdRef.current === selectedApplication.id) return;

    if (isRestoredSelectionWithCachedViewport(selectedApplication.id)) {
      markSelectionPanHandled(selectedApplication.id);
      return;
    }

    if (!isVisible || !mapRef.current) return;

    const poly = parcelPolygons.find((p) => p.appId === selectedApplication.id);
    if (!poly || !Array.isArray(poly.pathsList) || poly.pathsList.length === 0) return;

    prevSelectedApplicationIdRef.current = selectedApplication.id;
    lastPolyFitIdRef.current = selectedApplication.id;
    lastPannedIdRef.current = selectedApplication.id;
    pendingPanRef.current = null;
    const rings = poly.pathsList.map((x) => x.paths);
    setTimeout(() => fitMapToPaths(rings), 60);
  }, [selectedApplication?.id, parcelPolygons, isVisible, isRestoredSelectionWithCachedViewport, markSelectionPanHandled]);

  // Geocode fallback — pan to point if no polygon fit happened yet
  useEffect(() => {
    if (!selectedApplication?.id) return;
    if (lastPannedIdRef.current === selectedApplication.id) return;

    if (isRestoredSelectionWithCachedViewport(selectedApplication.id)) {
      markSelectionPanHandled(selectedApplication.id);
      return;
    }

    const lat = selectedApplication.geocode_lat;
    const lng = selectedApplication.geocode_lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;

    prevSelectedApplicationIdRef.current = selectedApplication.id;

    if (isVisible && mapRef.current) {
      lastPannedIdRef.current = selectedApplication.id;
      const map = mapRef.current;
      setTimeout(() => {
        map.panTo({ lat, lng });
        if (map.getZoom() < 13) map.setZoom(13);
      }, 60);
    } else {
      pendingPanRef.current = { type: 'point', lat, lng };
    }
  }, [selectedApplication?.id, isVisible, isRestoredSelectionWithCachedViewport, markSelectionPanHandled]);

  // When map tab becomes visible and there's a pending pan from another tab, apply it.
  useEffect(() => {
    if (!isVisible || !mapRef.current || !pendingPanRef.current) return;

    if (selectedApplication?.id && isRestoredSelectionWithCachedViewport(selectedApplication.id)) {
      markSelectionPanHandled(selectedApplication.id);
      return;
    }

    const map = mapRef.current;
    const p = pendingPanRef.current;
    lastPannedIdRef.current = selectedApplication?.id || null;
    if (window.google?.maps?.event) {
      window.google.maps.event.trigger(map, 'resize');
    }
    setTimeout(() => {
      if (p.type === 'point') {
        map.panTo({ lat: p.lat, lng: p.lng });
        if (map.getZoom() < 13) map.setZoom(13);
      } else if (p.type === 'paths' && Array.isArray(p.rings)) {
        fitMapToPaths(p.rings);
      }
      pendingPanRef.current = null;
    }, 100);
  }, [isVisible, selectedApplication?.id, isRestoredSelectionWithCachedViewport, markSelectionPanHandled]);

  // Explicit "Show on map" — same parcel fitBounds as first select (nonce re-triggers even if already selected).
  const lastHandledMapFocusNonceRef = useRef(0);
  useEffect(() => {
    if (!mapFocusNonce) return;
    if (mapFocusNonce === lastHandledMapFocusNonceRef.current) return;
    if (!selectedApplication?.id) return;

    const runFlash = () => {
      flashTimersRef.current.forEach((id) => clearTimeout(id));
      flashTimersRef.current = [];
      setFlashOn(false);
      setFlashPinId(selectedApplication.id);
      const period = 1200;
      const onFraction = 0.45;
      const pulses = 6;
      for (let i = 0; i < pulses; i++) {
        const tOn = setTimeout(() => setFlashOn(true), Math.floor(i * period));
        const tOff = setTimeout(() => setFlashOn(false), Math.floor(i * period + period * onFraction));
        flashTimersRef.current.push(tOn, tOff);
      }
      flashTimersRef.current.push(setTimeout(() => setFlashPinId(null), pulses * period + 100));
    };

    const poly = parcelPolygons.find((p) => p.appId === selectedApplication.id);
    if (poly && Array.isArray(poly.pathsList) && poly.pathsList.length > 0) {
      const rings = poly.pathsList.map((x) => x.paths);
      lastPolyFitIdRef.current = selectedApplication.id;
      lastPannedIdRef.current = selectedApplication.id;
      lastHandledMapFocusNonceRef.current = mapFocusNonce;
      if (!isVisible || !mapRef.current) {
        pendingPanRef.current = { type: 'paths', rings };
        return;
      }
      runFlash();
      if (window.google?.maps?.event) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
      setTimeout(() => fitMapToPaths(rings), 60);
      return;
    }

    // No parcel yet — wait for parcelPolygons to update (same as select-time fit).
    // Geocode fallback only if present; most apps are parcel-only.
    const lat = selectedApplication.geocode_lat;
    const lng = selectedApplication.geocode_lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;

    lastPannedIdRef.current = selectedApplication.id;
    lastHandledMapFocusNonceRef.current = mapFocusNonce;
    if (!isVisible || !mapRef.current) {
      pendingPanRef.current = { type: 'point', lat, lng };
      return;
    }
    runFlash();
    const map = mapRef.current;
    setTimeout(() => {
      map.panTo({ lat, lng });
      if (map.getZoom() < 13) map.setZoom(13);
    }, 60);
  }, [mapFocusNonce, selectedApplication, parcelPolygons, isVisible]);

  // no external highlight overlay; the selected pin will animate itself

  if (backgroundMode) {
    return (
      <Box sx={{ width: '100%', height: '100%', position: 'fixed', inset: 0, zIndex: 0, maxWidth: '100%', overflow: 'hidden' }}>
        {loadError && (
          <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 2, bgcolor: 'white', p: 2, borderRadius: 2 }}>
            <Typography color="error">Failed to load Google Maps. Please check your API key.</Typography>
          </Box>
        )}
        {!isLoaded && !loadError && (
          <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 2, bgcolor: 'white', px: 2, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CircularProgress size={18} sx={{ color: '#7454A5' }} />
            <Typography variant="body2" sx={{ color: '#7454A5', fontWeight: 600 }}>Loading map…</Typography>
          </Box>
        )}
        {isLoaded && !dataReady && (
          <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 2, bgcolor: 'white', px: 2, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CircularProgress size={18} sx={{ color: '#7454A5' }} />
            <Typography variant="body2" sx={{ color: '#7454A5', fontWeight: 600 }}>Loading data…</Typography>
          </Box>
        )}
        {isLoaded && (
            <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={mapOptions}
            onLoad={map => { 
              mapRef.current = map;
              map.setCenter(mapCenter);
              map.setZoom(mapZoom);
              setMapInstanceReady(true);
              attachMouseMoveListener(map); 
              attachSentryMapListeners(map, setZoomStyleTier, mapZoom, {
                onIdle: () => persistViewportFromMap(map),
              });
            }}
            onClick={(e) => {
              if (pinnedParcelPickerRef.current) {
                dismissPinnedPicker();
                return;
              }
              if (!e.latLng || !onPinClick) return;
              const clickedLat = e.latLng.lat();
              const clickedLng = e.latLng.lng();
              logger.debug('Map click', {
                view: 'map',
                lat: clickedLat,
                lng: clickedLng,
              });
              
              // Find nearest pin (dynamic threshold by zoom)
              let nearest = null;
              let nearestDist = Number.POSITIVE_INFINITY;
              for (const pin of geocodedPins) {
                const dist = Math.sqrt(
                  Math.pow(pin.lat - clickedLat, 2) +
                  Math.pow(pin.lng - clickedLng, 2)
                );
                if (dist < nearestDist) {
                  nearestDist = dist;
                  nearest = pin;
                }
              }
              const threshold = getClickThresholdDeg();
              logger.debug('Nearest map pin', {
                view: 'map',
                application_id: nearest?.id ? String(nearest.id) : undefined,
                distance_deg: nearestDist,
                threshold_deg: threshold,
              });
              if (nearest && nearestDist < threshold) {
                logger.debug('Map pin detected from click', {
                  view: 'map',
                  application_id: String(nearest.id),
                  distance_deg: nearestDist,
                });
                if (nearest.clickHandler) nearest.clickHandler();
              }
            }}
          >
            {/* All region boundaries (user-accessible) */}
            {showCountyBoundaries && regions && regions.map((region) => {
              const slug = region.slug;
              const paths = slug ? regionBoundaries[slug] : null;
              if (!paths || paths.length === 0) return null;
              const isHovered = slug && slug === hoveredRegionSlug;
              const isSelected = slug && slug === selectedRegionSlug;
              const hasAccess = slug && accessibleSlugSet.has(slug);

              const fillColor = hasAccess ? PURPLE : '#333333';
              const strokeColor = hasAccess ? PURPLE : '#757575';
              const fillOpacity = isHovered ? 0.30 : 0.22;
              const strokeOpacity = isHovered ? 1.0 : 0.8;
              const strokeWeight = isHovered ? 3 : 2;

              return (
                <Polygon
                  key={`region-${slug}`}
                  paths={paths}
                  onMouseOver={() => setHoveredRegionSlug(slug)}
                  onMouseOut={() => setHoveredRegionSlug(prev => (prev === slug ? null : prev))}
                  onClick={() => {
                    if (onRegionSelect && slug) {
                      onRegionSelect(slug);
                    }
                  }}
                  options={{
                    fillColor,
                    fillOpacity,
                    strokeColor,
                    strokeOpacity,
                    strokeWeight,
                    clickable: true,
                    zIndex: isSelected ? 20 : 10,
                  }}
                />
              );
            })}

            {/* Municipality boundaries */}
            {showMuniBoundaries && Object.entries(muniBoundaryPaths).map(([muniId, paths]) => {
              const isHovered = muniId === hoveredMuniId;
              const isPublished = muniBoundaryPublished[muniId] !== false;
              const color = isPublished ? MUNI_COLOR : '#9ca3af';
              return (
                <Polygon
                  key={`muni-bg-${muniId}`}
                  paths={paths}
                  onMouseOver={() => setHoveredMuniId(muniId)}
                  onMouseOut={() => setHoveredMuniId(prev => (prev === muniId ? null : prev))}
                  options={{
                    fillColor: color,
                    fillOpacity: isHovered ? 0.30 : 0.12,
                    strokeColor: color,
                    strokeOpacity: isHovered ? 1.0 : 0.7,
                    strokeWeight: isHovered ? 3 : 1.5,
                    clickable: true,
                    zIndex: isHovered ? 35 : 25,
                  }}
                />
              );
            })}

            {/* County name tooltip on hover */}
            {hoveredRegionSlug && regionBoundaries[hoveredRegionSlug] && (() => {
              const pos = getPathsBBoxTopCenter(regionBoundaries[hoveredRegionSlug]);
              if (!pos) return null;
              const region = regions?.find(r => r.slug === hoveredRegionSlug);
              const label = region ? (region.state ? `${region.name}, ${region.state}` : region.name) : hoveredRegionSlug;
              return <PolygonTooltip position={pos} label={label} accentColor={PURPLE} />;
            })()}

            {/* Municipality name tooltip on hover */}
            {hoveredMuniId && muniBoundaryPaths[hoveredMuniId] && (() => {
              const pos = getPathsBBoxTopCenter(muniBoundaryPaths[hoveredMuniId]);
              if (!pos) return null;
              const isPublished = muniBoundaryPublished[hoveredMuniId] !== false;
              const name = muniBoundaryNames[hoveredMuniId] || 'Municipality';
              const label = isPublished ? name : `${name} — Not covered by LandSentry at this time`;
              const color = isPublished ? MUNI_COLOR : '#9ca3af';
              return <PolygonTooltip position={pos} label={label} accentColor={color} />;
            })()}

            {/* Parcel polygons (preferred when available) */}
            {parcelPolygons
              .filter(p => includeAllDates || !playbackDate || (p.firstDate && p.firstDate <= playbackDate))
              .sort((a, b) => compareParcelLayersForRender(a, b, selectedApplication?.id))
              .map((p, idx) => {
                const isSelected = !!(selectedApplication && selectedApplication.id === p.appId);
                return (
                  <ParcelPolygonLayers
                    key={`parcel-app-${p.appId}`}
                    parcel={p}
                    isSelected={isSelected}
                    zIndex={getParcelLayerZIndex(p, idx, isSelected)}
                    onClick={handleParcelPolygonClick}
                    onMouseOver={handleParcelPolygonHover}
                    onMouseMove={handleParcelPolygonHover}
                    onMouseOut={handleParcelPolygonHoverEnd}
                    onRightClick={handleParcelPolygonContextMenu}
                  />
                );
              })}

            {geocodedPins
              .filter(pin => includeAllDates || !playbackDate || (pin.firstDate && pin.firstDate <= playbackDate))
              .map(pin => {
              const appStyle = getApplicationStyle(getPrimaryApplicationType(pin.application));
              const isSelected = !!(selectedApplication && selectedApplication.id === pin.id);
              const isFlashing = !!(flashPinId && flashOn && flashPinId === pin.id);
              const showWhite = isSelected || isFlashing;
              const pinSvg = `
                <svg width="48" height="60" viewBox="0 0 48 60" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;">
                  <path d="M24 4C15.2 4 8 11.2 8 20c0 12 16 32 16 32s16-20 16-32c0-8.8-7.2-16-16-16z" 
                        fill="${showWhite ? '#FFFFFF' : appStyle.color}" stroke="${showWhite ? appStyle.color : 'white'}" stroke-width="2" pointer-events="none"/>
                  <text x="24" y="25" font-family="Arial, sans-serif" font-size="16" font-weight="bold" 
                        text-anchor="middle" fill="${showWhite ? appStyle.color : 'white'}" pointer-events="none">${appStyle.letter}</text>
                  ${ isSelected ? `
                  <polygon points="8,0 40,0 24,12" fill="${appStyle.color}" stroke="white" stroke-width="1" opacity="1" pointer-events="none" />
                  <polygon points="10,0 38,0 24,12" fill="#FFFFFF" stroke="${appStyle.color}" stroke-width="2" opacity="1" pointer-events="none" />
                  ` : ''}
                </svg>
              `;
              
                return (
                  <Marker
                    key={pin.id}
                    position={{ lat: pin.lat, lng: pin.lng }}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg)}`,
                      scaledSize: window.google?.maps ? new window.google.maps.Size(32, 40) : { width: 32, height: 40 },
                      anchor: window.google?.maps ? new window.google.maps.Point(16, 40) : { x: 16, y: 40 },
                    }}
                    
                    onClick={() => {
                      logger.debug('Marker clicked', { view: 'map', application_id: String(pin.id) });
                      if (pin.clickHandler) pin.clickHandler();
                    }}
                    onLoad={(marker) => {
                      if (!marker) return;
                      try {
                        marker.addListener('click', () => {
                          logger.debug('Native marker click', { view: 'map', application_id: String(pin.id) });
                          if (pin.clickHandler) pin.clickHandler();
                        });
                        marker.addListener('mouseover', (e) => {
                          setHoveredParcelHit(null);
                          setHoveredPin({ ...pin, pixel: getHoverPixelFromEvent(e?.domEvent, e?.latLng) });
                        });
                        marker.addListener('mouseout', () => setHoveredPin(prev => (prev && prev.id === pin.id ? null : prev)));
                      } catch {}
                    }}
                    options={{ optimized: true, clickable: true, zIndex: isSelected ? 1100 : 100 }}
                  />
              );
            })}
            
            {/* County-anchored HUD banner using OverlayView */}
            {county && countyPolygons[county] && (() => {
              const labelData = getCountyLabelAnchor(county)
              if (!labelData) return null
              const anchorPos = labelData.anchor
              return (
                <OverlayView
                  position={anchorPos}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({ x: -width / 2, y: -height - 8 })}
                >
                  <div style={{ pointerEvents: 'auto' }}>
                    <HudBanner countyName={county} hiddenCount={hiddenCount} anchored />
                  </div>
                </OverlayView>
              )
            })()}

          </GoogleMap>
        )}
        {isLoaded && (
          <MapTopRightControls
            mapType={mapType}
            onMapTypeChange={setMapType}
            showCountyBoundaries={showCountyBoundaries}
            onCountyChange={setShowCountyBoundaries}
            showMuniBoundaries={showMuniBoundaries}
            onMuniChange={setShowMuniBoundaries}
            compact={compactChrome}
          />
        )}
        {renderCaseHoverOverlays()}
      </Box>
    )
  }

  // Embedded mode: fill parent container
  if (embedded) {
    return (
      <Box sx={{ width: '100%', height: mapHeight || '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        {showExpandFilters && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              borderBottom: '1px solid #e5e7eb',
              bgcolor: '#fafafa',
              boxSizing: 'border-box',
              flexShrink: 0,
              minHeight: PANEL_CHROME_HEADER_H,
              height: PANEL_CHROME_HEADER_H,
            }}
          >
            <IconButton
              size="small"
              onClick={onExpandFilters}
              aria-label="Expand filters"
              sx={{
                color: '#4b5563',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                p: 0.5,
                bgcolor: '#fff',
                '&:hover': { bgcolor: '#f3f4f6' },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {!isInitiallyReady && (
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f8f9fa' }}>
            <CircularProgress size={28} sx={{ color: '#7454A5' }} />
          </Box>
        )}
        <Box sx={{ width: '100%', height: '100%', visibility: isInitiallyReady ? 'visible' : 'hidden' }}>
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={mapOptions}
            onLoad={map => { 
              mapRef.current = map;
              map.setCenter(mapCenter);
              map.setZoom(mapZoom);
              setMapInstanceReady(true);
              attachMouseMoveListener(map); 
              attachSentryMapListeners(map, setZoomStyleTier, mapZoom, {
                onIdle: () => persistViewportFromMap(map),
              });
            }}
            onClick={(e) => {
              if (pinnedParcelPickerRef.current) {
                dismissPinnedPicker();
                return;
              }
              if (!e.latLng || !onPinClick) return;
              const map = mapRef.current;
              if (!map) return;
              const nearest = findNearestPinByPixels(map, e.latLng);
              logger.debug('Map click (pixel distance)', {
                view: 'map',
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
                nearest_px: nearest?.distPx,
              });
              if (nearest && nearest.distPx < 22) {
                if (nearest.pin.clickHandler) nearest.pin.clickHandler();
              }
            }}
          >
            {/* All region boundaries, colored by access status */}
            {showCountyBoundaries && regions && regions.map((region) => {
              const slug = region.slug;
              const paths = slug ? regionBoundaries[slug] : null;
              if (!paths || paths.length === 0) return null;
              const isHovered = slug && slug === hoveredRegionSlug;
              const isSelected = slug && slug === selectedRegionSlug;
              const hasAccess = slug && accessibleSlugSet.has(slug);

              const fillColor = hasAccess ? PURPLE : '#333333';
              const strokeColor = hasAccess ? PURPLE : '#757575';
              const fillOpacity = isHovered ? 0.30 : 0.22;
              const strokeOpacity = isHovered ? 1.0 : 0.8;
              const strokeWeight = isHovered ? 3 : 2;

              return (
                <Polygon
                  key={`region-${slug}`}
                  paths={paths}
                  onMouseOver={() => setHoveredRegionSlug(slug)}
                  onMouseOut={() => setHoveredRegionSlug(prev => (prev === slug ? null : prev))}
                  onClick={() => {
                    if (onRegionSelect && slug) {
                      onRegionSelect(slug);
                    }
                  }}
                  options={{
                    fillColor,
                    fillOpacity,
                    strokeColor,
                    strokeOpacity,
                    strokeWeight,
                    clickable: true,
                    zIndex: isSelected ? 20 : 10,
                  }}
                />
              );
            })}

            {/* Municipality boundaries */}
            {showMuniBoundaries && Object.entries(muniBoundaryPaths).map(([muniId, paths]) => {
              const isHovered = muniId === hoveredMuniId;
              const isPublished = muniBoundaryPublished[muniId] !== false;
              const color = isPublished ? MUNI_COLOR : '#9ca3af';
              return (
                <Polygon
                  key={`muni-embed-${muniId}`}
                  paths={paths}
                  onMouseOver={() => setHoveredMuniId(muniId)}
                  onMouseOut={() => setHoveredMuniId(prev => (prev === muniId ? null : prev))}
                  options={{
                    fillColor: color,
                    fillOpacity: isHovered ? 0.30 : 0.12,
                    strokeColor: color,
                    strokeOpacity: isHovered ? 1.0 : 0.7,
                    strokeWeight: isHovered ? 3 : 1.5,
                    clickable: true,
                    zIndex: isHovered ? 35 : 25,
                  }}
                />
              );
            })}

            {/* County name tooltip on hover */}
            {hoveredRegionSlug && regionBoundaries[hoveredRegionSlug] && (() => {
              const pos = getPathsBBoxTopCenter(regionBoundaries[hoveredRegionSlug]);
              if (!pos) return null;
              const region = regions?.find(r => r.slug === hoveredRegionSlug);
              const label = region ? (region.state ? `${region.name}, ${region.state}` : region.name) : hoveredRegionSlug;
              return <PolygonTooltip position={pos} label={label} accentColor={PURPLE} />;
            })()}

            {/* Municipality name tooltip on hover */}
            {hoveredMuniId && muniBoundaryPaths[hoveredMuniId] && (() => {
              const pos = getPathsBBoxTopCenter(muniBoundaryPaths[hoveredMuniId]);
              if (!pos) return null;
              const isPublished = muniBoundaryPublished[hoveredMuniId] !== false;
              const name = muniBoundaryNames[hoveredMuniId] || 'Municipality';
              const label = isPublished ? name : `${name} — Not covered by LandSentry at this time`;
              const color = isPublished ? MUNI_COLOR : '#9ca3af';
              return <PolygonTooltip position={pos} label={label} accentColor={color} />;
            })()}

            {/* Parcel polygons (preferred when available) */}
            {parcelPolygons
              .filter(p => includeAllDates || !playbackDate || (p.firstDate && p.firstDate <= playbackDate))
              .sort((a, b) => compareParcelLayersForRender(a, b, selectedApplication?.id))
              .map((p, idx) => {
                const isSelected = !!(selectedApplication && selectedApplication.id === p.appId);
                return (
                  <ParcelPolygonLayers
                    key={`parcel-embed-${p.appId}`}
                    parcel={p}
                    isSelected={isSelected}
                    zIndex={getParcelLayerZIndex(p, idx, isSelected)}
                    onClick={handleParcelPolygonClick}
                    onMouseOver={handleParcelPolygonHover}
                    onMouseMove={handleParcelPolygonHover}
                    onMouseOut={handleParcelPolygonHoverEnd}
                    onRightClick={handleParcelPolygonContextMenu}
                  />
                );
              })}

            {geocodedPins
              .filter(pin => includeAllDates || !playbackDate || (pin.firstDate && pin.firstDate <= playbackDate))
              .map(pin => {
                const appStyle = getApplicationStyle(getPrimaryApplicationType(pin.application));
                const isSelected = !!(selectedApplication && selectedApplication.id === pin.id);
                const isFlashing = !!(flashPinId && flashOn && flashPinId === pin.id);
                const showWhite = isSelected || isFlashing;
                const pinSvg = `
                  <svg width="48" height="60" viewBox="0 0 48 60" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;">
                    <path d="M24 4C15.2 4 8 11.2 8 20c0 12 16 32 16 32s16-20 16-32c0-8.8-7.2-16-16-16z" 
                          fill="${showWhite ? '#FFFFFF' : appStyle.color}" stroke="${showWhite ? appStyle.color : 'white'}" stroke-width="2" pointer-events="none"/>
                    <text x="24" y="25" font-family="Arial, sans-serif" font-size="16" font-weight="bold" 
                          text-anchor="middle" fill="${showWhite ? appStyle.color : 'white'}" pointer-events="none">${appStyle.letter}</text>
                    ${isSelected ? `
                    <polygon points="8,0 40,0 24,12" fill="${appStyle.color}" stroke="white" stroke-width="1" opacity="1" pointer-events="none" />
                    <polygon points="10,0 38,0 24,12" fill="#FFFFFF" stroke="${appStyle.color}" stroke-width="2" opacity="1" pointer-events="none" />
                    ` : ''}
                  </svg>
                `;
                return (
                  <Marker
                    key={pin.id}
                    position={{ lat: pin.lat, lng: pin.lng }}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg)}`,
                      scaledSize: window.google?.maps ? new window.google.maps.Size(32, 40) : { width: 32, height: 40 },
                      anchor: window.google?.maps ? new window.google.maps.Point(16, 40) : { x: 16, y: 40 },
                    }}
                    
                    onClick={() => {
                      logger.debug('Marker clicked', { view: 'map', application_id: String(pin.id) });
                      if (pin.clickHandler) pin.clickHandler();
                    }}
                    onLoad={(marker) => {
                      if (!marker) return;
                      try {
                        marker.addListener('click', () => {
                          logger.debug('Native marker click', { view: 'map', application_id: String(pin.id) });
                          if (pin.clickHandler) pin.clickHandler();
                        });
                        marker.addListener('mouseover', (e) => {
                          setHoveredParcelHit(null);
                          setHoveredPin({ ...pin, pixel: getHoverPixelFromEvent(e?.domEvent, e?.latLng) });
                        });
                        marker.addListener('mouseout', () => setHoveredPin(prev => (prev && prev.id === pin.id ? null : prev)));
                      } catch {}
                    }}
                    options={{ optimized: true, clickable: true, zIndex: isSelected ? 1100 : 100 }}
                  />
                );
              })}

            {/* County-anchored HUD banner using OverlayView */}
            {county && countyPolygons[county] && (() => {
              const labelData = getCountyLabelAnchor(county)
              if (!labelData) return null
              const anchorPos = labelData.anchor
              return (
                <OverlayView
                  position={anchorPos}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => {
                    return { x: -Math.round(width / 2), y: -height - 8 }
                  }}
                >
                  <Box sx={{ display: 'inline-block', transform: 'translateX(-50%)' }}>
                    <HudBanner countyName={county} hiddenCount={hiddenCount} anchored />
                  </Box>
                </OverlayView>
              )
            })()}

          </GoogleMap>
        )}
        </Box>
        {isLoaded && isInitiallyReady && (
          <MapTopRightControls
            mapType={mapType}
            onMapTypeChange={setMapType}
            showCountyBoundaries={showCountyBoundaries}
            onCountyChange={setShowCountyBoundaries}
            showMuniBoundaries={showMuniBoundaries}
            onMuniChange={setShowMuniBoundaries}
            compact={compactChrome}
          />
        )}
        {renderCaseHoverOverlays()}
        {loadError && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#141414',
              zIndex: 20,
              p: 2,
            }}
          >
            <Typography color="error" align="center">
              Failed to load Google Maps. Please check your API key.
            </Typography>
          </Box>
        )}
        </Box>
      </Box>
    )
  }

  return (
    <Paper elevation={3} sx={{ height: 400, mb: 3, p: 2, border: '2px solid #7454A5' }}>
      <Typography variant="h6" sx={{ color: '#7454A5', fontWeight: 700 }}>Interactive Map</Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Selected County: {county || 'None'}
      </Typography>
      <Typography variant="body2">
        Date Range: {dateRange.from || 'Start'} - {dateRange.to || 'End'}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        Data Types: {dataTypes && dataTypes.length > 0 ? dataTypes.join(', ') : 'None selected'}
      </Typography>
      <Box sx={{ mt: 2 }}>
        {loadError && (
          <Typography color="error">Failed to load Google Maps. Please check your API key.</Typography>
        )}
        {!isLoaded && !loadError && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
            <CircularProgress size={18} sx={{ color: '#7454A5' }} />
            <Typography variant="body2" sx={{ color: '#7454A5', fontWeight: 600 }}>Loading map…</Typography>
          </Box>
        )}
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            options={mapOptions}
            onLoad={map => {
              mapRef.current = map;
              map.setCenter(mapCenter);
              map.setZoom(mapZoom);
              setMapInstanceReady(true);
            }}
          >
            {/* All region boundaries (user-accessible) */}
            {showCountyBoundaries && regions && regions.map((region) => {
              const slug = region.slug;
              const paths = slug ? regionBoundaries[slug] : null;
              if (!paths || paths.length === 0) return null;
              const isSelected = slug && slug === selectedRegionSlug;
              const hasAccess = slug && accessibleSlugSet.has(slug);

              const fillColor = hasAccess ? PURPLE : '#333333';
              const strokeColor = hasAccess ? PURPLE : '#757575';
              const fillOpacity = isSelected ? 0.30 : 0.22;
              const strokeOpacity = 0.8;
              const strokeWeight = 2;

              return (
                <Polygon
                  key={`region-${slug}`}
                  paths={paths}
                  options={{
                    fillColor,
                    fillOpacity,
                    strokeColor,
                    strokeOpacity,
                    strokeWeight,
                    clickable: false,
                    zIndex: isSelected ? 20 : 10,
                  }}
                />
              );
            })}

            {/* Legacy county polygons disabled now that we use region-based tiles */}
            
            {/* Selected property is used for pan/zoom only; no overlay marker */}

            {/* All Application Pins (colored with letters) */}
            {/* Parcel polygons (preferred when available) */}
            {parcelPolygons
              .filter(p => includeAllDates || !playbackDate || (p.firstDate && p.firstDate <= playbackDate))
              .sort((a, b) => compareParcelLayersForRender(a, b, selectedApplication?.id))
              .map((p, idx) => {
                const isSelected = !!(selectedApplication && selectedApplication.id === p.appId);
                return (
                  <ParcelPolygonLayers
                    key={`parcel-small-${p.appId}`}
                    parcel={p}
                    isSelected={isSelected}
                    zIndex={getParcelLayerZIndex(p, idx, isSelected)}
                    interactive={false}
                  />
                );
              })}

            {geocodedPins
              .filter(pin => includeAllDates || !playbackDate || (pin.firstDate && pin.firstDate <= playbackDate))
              .map(pin => {
              const appStyle = getApplicationStyle(getPrimaryApplicationType(pin.application));
              const isSelected = !!(selectedApplication && selectedApplication.id === pin.id);
              const isFlashing = !!(flashPinId && flashOn && flashPinId === pin.id);
              const showWhite = isSelected || isFlashing;
              const pinSvg = `
                <svg width="48" height="60" viewBox="0 0 48 60" xmlns="http://www.w3.org/2000/svg" style="pointer-events: none;">
                  <path d="M24 4C15.2 4 8 11.2 8 20c0 12 16 32 16 32s16-20 16-32c0-8.8-7.2-16-16-16z" 
                        fill="${showWhite ? '#FFFFFF' : appStyle.color}" stroke="${showWhite ? appStyle.color : 'white'}" stroke-width="2" pointer-events="none"/>
                  <text x="24" y="25" font-family="Arial, sans-serif" font-size="16" font-weight="bold" 
                        text-anchor="middle" fill="${showWhite ? appStyle.color : 'white'}" pointer-events="none">${appStyle.letter}</text>
                  ${isSelected ? `
                  <polygon points="8,0 40,0 24,12" fill="${appStyle.color}" stroke="white" stroke-width="1" opacity="1" pointer-events="none" />
                  <polygon points="10,0 38,0 24,12" fill="#FFFFFF" stroke="${appStyle.color}" stroke-width="2" opacity="1" pointer-events="none" />
                  ` : ''}
                </svg>
              `;
              
                return (
                  <Marker
                    key={pin.id}
                    position={{ lat: pin.lat, lng: pin.lng }}
                    icon={{
                      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(pinSvg)}`,
                      scaledSize: window.google?.maps ? new window.google.maps.Size(32, 40) : { width: 32, height: 40 },
                      anchor: window.google?.maps ? new window.google.maps.Point(16, 40) : { x: 16, y: 40 },
                    }}
                    title={`${pin.application.application_type}: ${pin.title}`}
                    onClick={() => {
                      logger.debug('Marker clicked', { view: 'map', application_id: String(pin.id) });
                      if (pin.clickHandler) pin.clickHandler();
                    }}
                    onLoad={(marker) => {
                      if (!marker) return;
                      try {
                        marker.addListener('click', () => {
                          logger.debug('Native marker click', { view: 'map', application_id: String(pin.id) });
                          if (pin.clickHandler) pin.clickHandler();
                        });
                      } catch {}
                    }}
                    onUnmount={(marker) => {
                      try {
                        // No-op; google automatically clears listeners on remove
                      } catch {}
                    }}
                    options={{ optimized: true, clickable: true, zIndex: isSelected ? 1100 : 100 }}
                  />
              );
            })}
          </GoogleMap>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: '#7454A5', mt: 1, display: 'block' }}>
        To enable the map, add your Google Maps API key in <b>src/components/MapView.jsx</b>.
      </Typography>
    </Paper>
  )
}

export default React.memo(MapView) 